import {PlayCircle} from '@mui/icons-material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {Box, IconButton, Tooltip, Typography} from '@mui/material';
import {useState} from 'react';

import {SPEED_FORMATS, toDisplaySpeed} from '../../../constants/common';
import {useAppSelector} from '../../../hooks/redux';
import {HazardEventLog} from '../../../interfaces/HazardAILog';
import DataGrid, {DataGridColumn} from '../../common/DataGrid';
import ModalFixed from '../../common/ModalFixed';
import {HazardDetectionMediaModal} from '../HazardAIDetectionLog/HazardDetectionMediaModal';
import {POSITION_LABELS} from './PositionTable';

interface Props {
  open: boolean;
  camId: number;
  items: HazardEventLog[];
  onClose: () => void;
}

const columns: DataGridColumn<HazardEventLog>[] = [
  {
    field: 'type',
    width: 40,
    headerName: 'Detection Status',
    resizeable: true,
    sortable: true,
    renderHeader: () => (
      <Tooltip title="Detection Status">
        <FiberManualRecordIcon />
      </Tooltip>
    ),
    renderCell: ({row}) =>
      row.type === 'detection' ? (
        <Tooltip title="Valid Detection">
          <FiberManualRecordIcon color="success" />
        </Tooltip>
      ) : (
        <Tooltip title="False Detection">
          <FiberManualRecordIcon color="error" />
        </Tooltip>
      ),
  },
  {
    field: 'timestamp',
    headerName: 'Timestamp',
    width: 160,
    resizeable: true,
    sortable: true,
  },
  {
    field: 'zone',
    headerName: 'Zone',
    width: 120,
    resizeable: true,
    sortable: true,
    renderCell: ({row}) => <Box textTransform="capitalize">{row.zone}</Box>,
  },
  {
    field: 'object',
    headerName: 'Object',
    width: 120,
    resizeable: true,
    sortable: true,
    renderCell: ({row}) => <Box textTransform="capitalize">{row.object}</Box>,
  },
  {
    field: 'confidence',
    headerName: 'Detection Confidence',
    width: 180,
    resizeable: true,
    sortable: true,
    renderCell: ({row}) => (
      <Box textTransform="capitalize">{row.confidence}</Box>
    ),
  },
  {
    field: 'raw_image',
    headerName: 'Detection Video',
    width: 140,
    resizeable: true,
    sortable: false,
    // renderCell is overridden by buildColumns to inject the onPlayClick callback
    renderCell: () => null,
  },
  {
    field: 'distance',
    headerName: 'Detection Distance',
    width: 160,
    resizeable: true,
    sortable: true,
    renderCell: ({row}) => row.distance,
  },
  {
    field: 'speed',
    headerName: 'Speed',
    width: 80,
    resizeable: true,
    sortable: true,
    renderCell: ({row}) => row.speed,
  },
  {
    field: 'latitude',
    headerName: 'Latitude',
    width: 120,
    resizeable: true,
    sortable: true,
    renderCell: ({row}) => row.latitude,
  },
  {
    field: 'longitude',
    headerName: 'Longitude',
    width: 120,
    resizeable: true,
    sortable: true,
    renderCell: ({row}) => row.longitude,
  },
];

// Build columns with a direct onPlayClick callback so we don't need CustomEvent tricks.
const buildColumns = (
  onPlayClick: (id: number) => void,
  speedFormat: (typeof SPEED_FORMATS)[number]
): DataGridColumn<HazardEventLog>[] =>
  columns.map((col) => {
    if (col.field === 'raw_image') {
      return {
        ...col,
        renderCell: ({row}) => (
          <Box width="100%" display="flex" justifyContent="center">
            <IconButton onClick={() => onPlayClick(row.id)}>
              <PlayCircle />
            </IconButton>
          </Box>
        ),
      };
    }
    if (col.field === 'speed') {
      return {
        ...col,
        headerName: `Speed (${speedFormat})`,
        renderCell: ({row}) =>
          row.speed != null ? toDisplaySpeed(Number(row.speed), speedFormat) : null,
      };
    }
    return col;
  });

export const CameraDetectionsModal = ({open, camId, items, onClose}: Props) => {
  const [mediaId, setMediaId] = useState<number | null>(null);
  const speedFormat = useAppSelector(({app}) => app.speedFormat);

  const positionLabel = POSITION_LABELS[camId] ?? `Camera ${camId}`;

  return (
    <>
      <ModalFixed open={open} width={Math.min(1100, window?.innerWidth - 60)}>
        <Box display="flex" flexDirection="column" height="80vh" p={2}>
          {/* Header */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={2}
            py={1}
            borderBottom="1px solid"
            borderColor="divider"
            flexShrink={0}
          >
            <Typography variant="h6" fontWeight="bold">
              {positionLabel} Camera — Detections ({items.length})
            </Typography>
            <IconButton size="small" onClick={onClose}>
              ✕
            </IconButton>
          </Box>

          {/* Detection list */}
          <Box flex={1} overflow="hidden" position="relative">
            <DataGrid
              rows={items}
              columns={buildColumns(setMediaId, speedFormat)}
              size="small"
              pagination
              paginationMode="client"
            />
          </Box>
        </Box>
      </ModalFixed>

      {mediaId != null && (
        <HazardDetectionMediaModal
          open
          id={mediaId}
          onClose={() => setMediaId(null)}
        />
      )}
    </>
  );
};

export default CameraDetectionsModal;
